import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, addDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../firebase';

interface Class {
  id: string;
  name: string;
  grade: string;
  teacher: string;
  teacherName?: string;
  studentCount: number;
  createdAt: string;
}

const Classes: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('All');
  
  // Form state for adding a new class
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [newClass, setNewClass] = useState({
    name: '',
    grade: '1',
    studentCount: 0
  });
  
  // Fetch classes from Firestore
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        
        // Query based on role (teachers see only their classes, admins see all)
        let classesQuery;
        if (userData?.role === 'teacher') {
          classesQuery = query(collection(db, 'classes'), where('teacher', '==', userData.uid));
        } else {
          classesQuery = collection(db, 'classes');
        }
        
        const snapshot = await getDocs(classesQuery);
        
        const fetchedClasses: Class[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          name: doc.data().name || 'Unnamed Class',
          grade: doc.data().grade || '1',
          teacher: doc.data().teacher || '',
          teacherName: doc.data().teacherName || '',
          studentCount: doc.data().studentCount || 0,
          createdAt: doc.data().createdAt || new Date().toISOString()
        } as Class));
        
        setClasses(fetchedClasses);
      } catch (err) {
        console.error('Error fetching classes:', err);
        setError('Failed to load classes');
      } finally {
        setLoading(false);
      }
    };

    fetchClasses();
  }, [userData]);

  // Add new class to Firestore
  const handleAddClass = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newClass.name || !newClass.grade) {
      setError(t('validation.required'));
      return;
    }
    
    try {
      setLoading(true);
      
      // Add the class to Firestore
      const classData = {
        ...newClass,
        teacher: userData?.uid,
        teacherName: userData?.displayName || userData?.email,
        studentCount: 0, // Start with 0 students
        createdAt: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, 'classes'), classData);
      
      // Add the new class to the local state
      setClasses(prevClasses => [
        ...prevClasses, 
        { 
          id: docRef.id, 
          ...classData,
          createdAt: new Date().toISOString()
        } as Class
      ]);
      
      // Reset the form
      setNewClass({
        name: '',
        grade: '1',
        studentCount: 0
      });
      
      setIsAddingClass(false);
    } catch (err) {
      console.error('Error adding class:', err);
      setError('Failed to add class');
    } finally {
      setLoading(false);
    }
  };

  // Delete a class from Firestore
  const handleDeleteClass = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this class?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Delete the class from Firestore
      await deleteDoc(doc(db, 'classes', id));
      
      // Remove the class from the local state
      setClasses(prevClasses => prevClasses.filter(cls => cls.id !== id));
    } catch (err) {
      console.error('Error deleting class:', err);
      setError('Failed to delete class');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter classes based on search and filters
  const filteredClasses = classes.filter(cls => {
    // Filter by search term
    const matchesSearch = cls.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by grade
    const matchesGrade = selectedGrade === 'All' || cls.grade === selectedGrade;
    
    return matchesSearch && matchesGrade;
  });
  
  // Get unique grades for filter
  const grades = ['All', ...new Set(classes.map(cls => cls.grade))];
  
  // Display loading state
  if (loading && classes.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#309D97]"></div>
      </div>
    );
  }
  
  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">{t('classes.management')}</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            className="float-right"
            onClick={() => setError('')}
          >
            &times;
          </button>
        </div>
      )}
      
      {/* Add Class Form */}
      {isAddingClass && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h2 className="text-lg font-medium mb-4">{t('classes.addClass')}</h2>
          <form onSubmit={handleAddClass}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('classes.name')}*</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newClass.name}
                  onChange={(e) => setNewClass({...newClass, name: e.target.value})}
                  required
                  placeholder="e.g. 3A, 5B, Science Group"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.grade')}*</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                  value={newClass.grade}
                  onChange={(e) => setNewClass({...newClass, grade: e.target.value})}
                  required
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(grade => (
                    <option key={grade} value={grade.toString()}>{t('students.gradeNumber', { number: grade })}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => setIsAddingClass(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-[#309D97] text-white rounded-md hover:bg-opacity-90"
                disabled={loading}
              >
                {loading ? t('classes.adding') : t('classes.addClass')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Filters and search */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.search')}</label>
            <input
              type="text"
              placeholder={t('classes.searchPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('content.grade')}</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
            >
              {grades.map(grade => (
                <option key={grade} value={grade}>
                  {grade === 'All' ? t('classes.allGrades') : t('students.gradeNumber', { number: grade })}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {t('classes.showing', { filtered: filteredClasses.length, total: classes.length })}
          </div>
          
          {(userData?.role === 'admin' || userData?.role === 'teacher') && (
            <button 
              className="bg-[#309D97] text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition"
              onClick={() => setIsAddingClass(!isAddingClass)}
            >
              {isAddingClass ? t('common.cancel') : t('classes.addClass')}
            </button>
          )}
        </div>
      </div>
      
      {/* Classes Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('classes.classInfo')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('students.gradeClass')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('classes.teacherInfo')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('classes.studentCount')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('students.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredClasses.map(cls => (
              <tr key={cls.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#309D97] bg-opacity-20 flex items-center justify-center text-[#309D97] font-medium">
                      {cls.name.charAt(0)}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{cls.name}</div>
                      <div className="text-sm text-gray-500">ID: {cls.id.slice(0, 8)}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{t('students.gradeNumber', { number: cls.grade })}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{cls.teacherName}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{cls.studentCount} {t('students.students')}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-[#309D97] hover:text-[#1e7d77] mr-3">
                    {t('classes.manageStudents')}
                  </button>
                  <button className="text-[#309D97] hover:text-[#1e7d77] mr-3">
                    {t('common.edit')}
                  </button>
                  {userData?.role === 'admin' && (
                    <button 
                      className="text-red-600 hover:text-red-800"
                      onClick={() => handleDeleteClass(cls.id)}
                      disabled={loading}
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            
            {filteredClasses.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  {classes.length === 0 
                    ? t('classes.noClasses') 
                    : t('classes.noMatchingClasses')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Classes; 